import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import DancerThumbnail from '../DancerThumbnail';
import { timeAgo } from '../utils/engagementKeys';
import './RankMovementWidgets.css';

const SESSION_KEY = 'rankMovementWidget:v1';
const SESSION_TTL_MS = 30 * 60 * 1000;

const readSession = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const { at, data } = JSON.parse(raw);
    return Date.now() - at < SESSION_TTL_MS ? data : null;
  } catch (e) {
    return null;
  }
};

const writeSession = (data) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ at: Date.now(), data }));
  } catch (e) {
    // storage unavailable – skip caching
  }
};

function Row({ name, children, badge }) {
  return (
    <li className="rmw-row">
      <DancerThumbnail dancerName={name} size={38} />
      <div className="rmw-info">
        <Link to={`/dancer/${encodeURIComponent(name)}`} className="rmw-name">{name}</Link>
        <div className="rmw-sub">{children}</div>
      </div>
      {badge}
    </li>
  );
}

const TABS = [
  { key: 'climbers', label: '📈 Biggest climbers' },
  { key: 'podium', label: '🆕 New on the podium' },
  { key: 'winners', label: '🏆 Latest winners' }
];

/** Homepage widgets fed by homepageWidgets/rankMovement (rebuilt every few hours by a Cloud Function). */
export default function RankMovementWidgets() {
  const [data, setData] = useState(() => readSession());
  const [tab, setTab] = useState('climbers');
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(!data);

  useEffect(() => {
    if (data) return undefined;
    let cancelled = false;
    getDoc(doc(db, 'homepageWidgets', 'rankMovement'))
      .then((snap) => {
        if (cancelled) return;
        if (snap.exists()) {
          const d = snap.data();
          const plain = {
            climbers: d.climbers || [],
            newOnPodium: d.newOnPodium || [],
            latestWinners: d.latestWinners || [],
            windowDays: d.windowDays || 30,
            updatedAtMs: d.updatedAt && d.updatedAt.toMillis ? d.updatedAt.toMillis() : null
          };
          setData(plain);
          writeSession(plain);
        }
      })
      .catch((e) => console.error('Rank movement widget failed:', e))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [data]);

  if (loading || !data) return null;

  const lists = {
    climbers: data.climbers,
    podium: data.newOnPodium,
    winners: data.latestWinners
  };
  if (!lists.climbers.length && !lists.podium.length && !lists.winners.length) return null;

  // Default to the first tab that has content
  const activeTab = lists[tab].length ? tab : (TABS.find((t) => lists[t.key].length) || TABS[0]).key;
  const allItems = lists[activeTab];
  const items = expanded ? allItems : allItems.slice(0, 6);

  return (
    <section className="rmw" aria-label="Ranking movement">
      <div className="rmw-head">
        <h3>🔥 What&apos;s moving</h3>
        <span className="rmw-window">last {data.windowDays} days{data.updatedAtMs ? ` · updated ${timeAgo(data.updatedAtMs)}` : ''}</span>
      </div>
      <div className="rmw-tabs">
        {TABS.filter((t) => lists[t.key].length).map((t) => (
          <button key={t.key} className={activeTab === t.key ? 'active' : ''} onClick={() => { setTab(t.key); setExpanded(false); }}>
            {t.label}
          </button>
        ))}
      </div>
      <ul className="rmw-list">
        {activeTab === 'climbers' && items.map((c) => (
          <Row
            key={`${c.dancerName}-${c.label}`}
            name={c.dancerName}
            badge={<span className="rmw-badge up">▲ {c.gain}</span>}
          >
            #{c.fromRank} → <strong>#{c.toRank}</strong> · {c.label}
          </Row>
        ))}
        {activeTab === 'podium' && items.map((c) => (
          <Row
            key={`${c.dancerName}-${c.label}`}
            name={c.dancerName}
            badge={<span className="rmw-badge podium">{['🥇', '🥈', '🥉'][c.toRank - 1] || `#${c.toRank}`}</span>}
          >
            {c.fromRank ? `from #${c.fromRank} ` : 'new entry '}· {c.label}
          </Row>
        ))}
        {activeTab === 'winners' && items.map((w) => (
          <Row
            key={`${w.dancerName}-${w.competitionId}-${w.label}`}
            name={w.dancerName}
            badge={<span className="rmw-badge podium">🥇</span>}
          >
            {w.label} · <Link to={`/competition/${encodeURIComponent(w.competitionId)}`}>{w.competitionName}</Link>
          </Row>
        ))}
      </ul>
      {allItems.length > 6 && (
        <button className="rmw-more" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Show less' : `Show all ${allItems.length}`}
        </button>
      )}
    </section>
  );
}
